'use client';

import { useCallback } from 'react';
import { useRouter } from 'next/navigation';
import storage from 'local-storage-fallback';
import {
  useStartImpersonationMutation,
  useStopImpersonationMutation,
} from 'src/api/redux/users';
import { useAppDispatch } from 'src/redux';
import { resetStore } from 'src/redux/actions/storeReset';
import { resetAllApiState } from 'src/redux/resetAllApiState';
import { useAuth } from 'src/context/auth';
import { AUTH } from 'src/constants';

/**
 * Custom hook for managing user impersonation functionality.
 * Provides methods to start and stop impersonating other users (admin-only).
 * Handles comprehensive store reset and cache clearing during impersonation transitions.
 *
 * @returns {Object} Impersonation control object
 * @returns {boolean} isLoading - True when impersonation request is in progress
 * @returns {Function} impersonate - Start impersonating a user by ID
 * @returns {Function} stopImpersonating - Stop current impersonation session
 * @returns {Function} resetImpersonationState - Reset all RTK Query caches and Redux state after an impersonation context switch
 *
 * @example
 * ```tsx
 * const { impersonate, stopImpersonating, isLoading } = useUserImpersonation();
 *
 * // Start impersonation
 * await impersonate(123);
 *
 * // Stop impersonation
 * await stopImpersonating();
 * ```
 */
const useUserImpersonation = () => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const { refreshLocalUser } = useAuth();

  const [
    startImpersonating,
    { isLoading: loadingStartImpersonation },
  ] = useStartImpersonationMutation();

  const [
    stopImpersonatingMutation,
    { isLoading: loadingStopImpersonating },
  ] = useStopImpersonationMutation();

  const clearUserSpecificLocalStorage = useCallback(() => {
    // Preserve only auth tokens - clear everything else. The fallback
    // backends don't support key()/length, so clear and restore the tokens.
    const accessToken = storage.getItem(AUTH.ACCESS_TOKEN);
    const refreshToken = storage.getItem(AUTH.REFRESH_TOKEN);

    storage.clear();

    if (accessToken !== null) storage.setItem(AUTH.ACCESS_TOKEN, accessToken);
    if (refreshToken !== null) storage.setItem(AUTH.REFRESH_TOKEN, refreshToken);
  }, []);

  /**
   * Resets all RTK Query caches and Redux slice state for an impersonation
   * context switch — used by both the UI button flow and the AI chat MCP flow.
   */
  const resetImpersonationState = useCallback(async () => {
    dispatch(resetStore());
    resetAllApiState(dispatch);
    await refreshLocalUser();
  }, [dispatch, refreshLocalUser]);

  const handleStartImpersonating = useCallback(
    async (userId: number) => {
      await startImpersonating({ userId }).unwrap();
      clearUserSpecificLocalStorage();
      await resetImpersonationState();
      await router.push('/');
    },
    [startImpersonating, router, clearUserSpecificLocalStorage, resetImpersonationState]
  );

  const handleStopImpersonating = useCallback(async () => {
    try {
      await stopImpersonatingMutation().unwrap();
    } catch (err) {
      if (err?.response?.status === 400) return;
      console.error(
        '[useUserImpersonation] Error stopping impersonation:',
        err
      );
    } finally {
      clearUserSpecificLocalStorage();
      await resetImpersonationState();
      await router.push('/');
    }
  }, [stopImpersonatingMutation, router, clearUserSpecificLocalStorage, resetImpersonationState]);

  return {
    isLoading: loadingStartImpersonation || loadingStopImpersonating,
    impersonate: handleStartImpersonating,
    stopImpersonating: handleStopImpersonating,
    resetImpersonationState,
  };
};

export default useUserImpersonation;
